<template>
    <div>
        <slot v-bind="{
            hasValidationErrors, hasUnsavedChanges, groupValidation 
        }"
        ></slot>
        <BaseStack
            spacing="2"
            :dividers="hideValidationDivider ? 'none' : 'between'"
        >
            <template
                v-show="hasValidationMessage && !hideValidationMessage"
            >
                <BaseInline items-center spacing="2">
                    <!-- invalid state -->
                    <BaseIcon
                        :class="validationMessageColorClass" 
                        :icon="validationMessageIcon" 
                        is-material
                    />
                    <!-- 
						Slots allow for custom html messages like this one in custom report builder
						<a class="u-flex-inline u-flex--center" href="#fields">
							<BaseIcon 
								icon="arrow_upward" 
								inline-position="beforeDense" 
								size="xs"
								is-material
							/>
							<span class="link-text">Report name and description are required</span>
						</a>    
					-->
                    <!-- @slot Replaces the default "saved" message -->
                    <slot v-if="hasSaved" name="savedMessage">
                        <span>{{ $t('Changes saved successfully!') }}</span>
                    </slot>
                    <!-- @slot Replaces the default "invalid" message -->
                    <slot v-else-if="hasValidationErrors"
                        name="invalidMessage"
                        v-bind="{
                            groupValidation
                        }"
                    >
                        <span>{{ $t('One or more required fields are invalid') }}</span>
                    </slot>
                    <!-- @slot Replaces the default "unsaved changes" message -->
                    <slot v-else-if="hasUnsavedChanges && !hideUnsavedMessage" name="unsavedChangesMessage">
                        <span class="u-txt-light">{{ $t('There are unsaved changes') }}</span>
                    </slot>
                </BaseInline>
            </template>
            ​
            <!-- @slot (scoped) use to add action buttons with form validation context -->
            <slot name="actions"
                v-bind="{
                    hasValidationErrors, hasUnsavedChanges, groupValidation 
                }"
            >
                <!-- Action buttons for the form go in here (e.g. "Save" or "Submit") -->
            </slot>
        </BaseStack>
    </div>
</template>

<script>

/* 
    FieldGroup = {
        name: groupName
        fields: [] array of fields
        message: validation message for the field
    }
*/

export default {
    name: 'BaseForm',
    props: {
        /**
         * Array of field data
         */
        fields: {
            type: Array,
            required: true,
        },
        /**
         * Array of field groups
         */
        fieldGroups: {
            type: Array,
            default() {
                return [];
            }
        },
        hasSaved: {
            type: Boolean,
            default: false
        },
        hideUnsavedMessage: {
            type: Boolean,
            default: false
        },
        hideValidationMessage: {
            type: Boolean,
            default: false
        },
        /**
        * (passthrough to BaseForm) Hides the divider between the validation and the form actions
        */
        hideValidationDivider: Boolean
    },
    data () {
        return {
            original: []
        };
    },
    computed: {
        fieldValues() {
            return JSON.stringify(this.fields.map((field) => field.value));
        },
        groupedFields() {
            const groupedFields = {};
            /* 
                {
                    groupid: [] array of fields
                }
            */
            this.fields.forEach((field) => {
                const {group} = field;
                if (group) {
                    if (!groupedFields[group.id]) {
                        groupedFields[group.id] = [];
                    }
                    groupedFields[group.id].push(field);
                }
            });

            return groupedFields;
        },
        groupValidation() {
            const map = {};

            Object.keys(this.groupedFields).forEach((groupId) => {
                const fields = this.groupedFields[groupId];
                map[groupId] = {
                    hasValidationErrors: this.getHasValidationErrors(fields),
                    message: fields.find((field) => field.group.message).group.message
                };
            });

            /*
                {
                    groupid: {
                        hasValidationErrors: boolean,
                        message: string (validation message)
                }
            */
            return map;
        },
        hasUnsavedChanges() {
            return this.fieldValues !== this.original;
        },
        hasValidationErrors() {
            // return after finding an invalid field
            return this.getHasValidationErrors(this.fields);
        },
        hasValidationMessage() {
            return this.hasValidationErrors || this.hasUnsavedChanges || this.hasSaved;
        },
        validationMessageColorClass() {
            if (this.hasSaved) {
                return 'u-txt-green';
            }
            if (this.hasValidationErrors) {
                return 'u-txt-red';
            }
            if (this.hasUnsavedChanges && !this.hideUnsavedMessage) {
                return 'u-txt-light';
            }
            return '';
        },
        validationMessageIcon() {
            if (this.hasSaved) {
                return 'check';
            }
            if (this.hasValidationErrors) {
                return 'error_outline';
            }
            if (this.hasUnsavedChanges && !this.hideUnsavedMessage) {
                return 'info';
            }
            return '';
        }
    },
    watch: {
        hasSaved: {
            handler() {
                if(this.hasSaved) {
                    this.setOriginalValues();
                }
            }
        }
    },
    mounted() {
        // save reference to original values to track changes
        this.setOriginalValues();
    },
    methods: {

        getHasValidationErrors(fields) {
            return !!fields.find((field) => {
                if (!field.rules) {
                    return;
                }
                // return after finding an invalid rule
                return !!field.rules.find((rule) => {
                    // assume rule is like: 
                    // v => !!v || 'Item is required'
                    return rule(field.value) !== true;
                });
            });
        },
        setOriginalValues() {
            this.original = this.fieldValues;
        }
    }
};
</script>